import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { mutate } from "swr";
import { putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { CreateEntryButton } from "@/components/create-entry-button";
import ReportDocDialog, {
  type ReportDocFormData,
} from "@/components/reports/report-doc-dialog";
import type { Version } from "@/types/domain";

// Create-report entry: the shared report-doc dialog, submitting to
// PUT /versions/:id { reportDoc } (backend creates the ReportDoc + fires the
// webhook). `pid` mode picks one of the app's versions; `version` mode
// (version detail page) locks the target version and hides the selector.
// autoOpenParam: ?report=1 (from the Inbox rollout-task Report button)
// opens the dialog on mount and drops the param.
export const CreateReportDialog = ({
  pid,
  version,
  autoOpenParam,
}: {
  pid?: string;
  version?: Version;
  autoOpenParam?: boolean;
}) => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(
    () => !!autoOpenParam && searchParams.get("report") === "1"
  );
  useEffect(() => {
    if (!autoOpenParam || searchParams.get("report") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("report");
    setSearchParams(next, { replace: true });
  }, [autoOpenParam, searchParams, setSearchParams]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (data: ReportDocFormData) => {
    const versionId = data.versionId;
    if (!versionId) return;
    setSubmitting(true);
    setError(null);
    try {
      await putFetcher(`/versions/${versionId}`, {
        arg: {
          reportDoc: {
            url: data.url,
            name: "",
            result: data.result,
            summary: data.summary,
          },
        },
      });
      toast({ title: "Report created" });
      setOpen(false);
      mutate(
        (key) =>
          Array.isArray(key) &&
          (key[0] === "/reports" || key[0] === `/versions/${versionId}`),
        undefined,
        { revalidate: true }
      );
    } catch (e) {
      setError((e as { message?: string })?.message || "Submit failed.");
      toastError(toast, "Create failed", e, "Submit failed.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CreateEntryButton label="New Report" onClick={() => setOpen(true)} />
      <ReportDocDialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setError(null);
        }}
        onSubmit={submit}
        pid={version ? undefined : pid}
        version={version}
        isSubmitting={submitting}
        error={error}
      />
    </>
  );
};

export default CreateReportDialog;
